/*
  Pocket: the product card photo swap, for phones.

  On a computer, hovering a product card swaps in its second photo (see
  component-card.css). Phones can't hover, so shoppers there never saw it. Here
  the cards resting in the middle of the screen change to their second photo
  once scrolling pauses, and change back as they scroll away, as if the pause
  were the pointer. Nothing changes while the page is moving, so a swipe past a
  dozen cards stays calm. Side by side cards change one after the other.

  Runs only on devices that can't hover, and never for shoppers whose phone
  asks for reduced motion. The fade and settle live in pocket-overrides.css.
*/
(() => {
  if (!window.matchMedia('(hover: none)').matches) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  // How long scrolling has to pause before the middle cards change.
  const REST_MS = 300;
  // Gap between side by side cards, so a row turns over left to right.
  const STAGGER_MS = 200;

  const inBand = new Set();
  let restTimer;

  const lightRestingCards = () => {
    const waiting = [...inBand].filter((media) => !media.classList.contains('is-scroll-lit'));
    const positions = new Map(waiting.map((media) => [media, media.getBoundingClientRect()]));
    waiting
      .sort((a, b) => positions.get(a).top - positions.get(b).top || positions.get(a).left - positions.get(b).left)
      .forEach((media, i) => {
        media.style.setProperty('--pocket-lit-delay', `${i * STAGGER_MS}ms`);
        media.classList.add('is-scroll-lit');
      });
  };

  const waitForRest = () => {
    clearTimeout(restTimer);
    restTimer = setTimeout(lightRestingCards, REST_MS);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting) {
          inBand.add(target);
        } else {
          inBand.delete(target);
          target.classList.remove('is-scroll-lit');
        }
      });
      waitForRest();
    },
    // The middle 40% of the screen; a photo counts once most of it is inside.
    { rootMargin: '-30% 0px -30% 0px', threshold: 0.6 }
  );

  window.addEventListener('scroll', waitForRest, { passive: true });

  const watchNewCards = () => {
    document.querySelectorAll('.media--hover-effect:not([data-pocket-scroll-lit])').forEach((media) => {
      media.dataset.pocketScrollLit = '';
      if (media.querySelectorAll('img').length > 1) observer.observe(media);
    });
  };

  watchNewCards();

  // Recommendations and some grids are added after the page loads.
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      watchNewCards();
    });
  }).observe(document.body, { childList: true, subtree: true });
})();
